import { useRef, type ReactNode } from 'react';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** One drag scope per array; IDs are stable item identities, never array indices. */
export function RecordEditorArraySorting({ ids, disabled, enabled, children, onMove }: {
  ids: string[]; disabled: boolean; enabled: boolean; children: ReactNode;
  onMove(itemId: string, beforeId?: string): void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const startedIds = useRef<string | null>(null);
  if (!enabled) return <>{children}</>;
  return <DndContext sensors={sensors} collisionDetection={closestCenter}
    onDragStart={() => { startedIds.current = disabled ? null : JSON.stringify(ids); }}
    onDragCancel={() => { startedIds.current = null; }}
    onDragEnd={({ active, over }) => {
      const snapshot = startedIds.current; startedIds.current = null;
      if (disabled || snapshot !== JSON.stringify(ids) || !over || active.id === over.id) return;
      const source = ids.indexOf(String(active.id)); const target = ids.indexOf(String(over.id));
      if (source < 0 || target < 0) return;
      onMove(ids[source], source < target ? ids[target + 1] : ids[target]);
    }}><SortableContext items={ids} strategy={verticalListSortingStrategy}>{children}</SortableContext></DndContext>;
}
export function RecordEditorSortableArrayItem({ id, disabled, label, children }: { id: string; disabled: boolean; label: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return <fieldset ref={setNodeRef} data-editor-sortable-item={id} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.65 : undefined }}>
    <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} disabled={disabled} aria-label={label} style={{ touchAction: 'none', cursor: disabled ? undefined : 'grab' }}>↕</button>
    {children}
  </fieldset>;
}
